/**
 * Whether a failed delivery is the page's problem or a person's.
 *
 * This was an allow-list of failure codes, and it was wrong four times in a
 * row: `MANIFEST_COUNT_CHANGED`, then the two account conditions, then a
 * failure with no code at all, then `DELIVERY_FAILED` - the executor's own
 * generic fallback, and so the likeliest code of all. Each fix added the code
 * that had just been missed.
 *
 * The contract says why that could never converge. `PackagingDelivery.
 * failureCode` is `type: string` with `pattern ^[A-Z][A-Z0-9_]*$` and there is
 * no enum for it anywhere in the document - it is an OPEN set. A client cannot
 * enumerate values the contract declines to enumerate, so any allow-list is
 * incomplete by construction and stays incomplete every time the executor
 * learns a new word.
 *
 * So the rule is stated the way it was always meant to be: two failures must
 * reach a person, and everything else is retried, whatever it turns out to be
 * called. A code this build has never seen retries - which is correct, because
 * a delivery that failed for a reason nobody has classified is precisely the
 * case where handing an operator the code teaches them nothing.
 *
 * It lives here rather than inside the component because the boundary is the
 * thing that kept moving. A predicate in a file with its own tests can be
 * proven; the same predicate spread across a JSX condition, a `recovering`
 * flag and an effect guard is three places to draw it differently, and it was
 * drawn differently in two of them.
 */

/**
 * The failures a machine must not clear.
 *
 * A copied object that did not match its source digest is an integrity
 * finding, and retrying it automatically would paper over the one thing this
 * product exists to prove. An empty package is a curation decision rather than
 * a fault, and no number of retries places a file in it.
 *
 * Nothing else belongs here. Adding a code to this list means asserting that a
 * person can do something about it that the page cannot.
 */
export const MUST_REACH_A_PERSON: readonly string[] = ['DELIVERED_COPY_DIGEST_MISMATCH', 'PACKAGE_EMPTY'];

/**
 * How many times the page will re-request one run's delivery.
 *
 * A cause that survives three attempts is not transient, and this is a copy of
 * every object in the package - 238 GB on the run that prompted it - so an
 * unbounded loop is worse than stopping.
 */
export const MAX_ATTEMPTS = 3;

/** The fields of the delivery record this decision reads, and no others. */
export interface DeliveryFacts {
  state?: string | null;
  failureCode?: string | null;
}

/**
 * Whether this record is a failure at all.
 *
 * One predicate, so the card and the retry cannot disagree about it. They did:
 * the card read `state === 'FAILED' || Boolean(failureCode)` while the retry
 * tested the code against a list, so a record could render as failed and be
 * ineligible for the recovery that was supposed to cover it.
 */
export function hasFailed(delivery: DeliveryFacts | null | undefined): boolean {
  if (!delivery) return false;
  return delivery.state === 'FAILED' || Boolean(delivery.failureCode);
}

/** Whether this particular failure is one the page clears itself. */
export function isRecoverable(delivery: DeliveryFacts | null | undefined): boolean {
  if (!hasFailed(delivery)) return false;
  const code = delivery?.failureCode ?? null;
  // No code is recoverable by the same rule: it is not one of the two, so it
  // is retried. It needs no branch of its own any more.
  return !(code !== null && MUST_REACH_A_PERSON.includes(code));
}

/**
 * The key the attempt counter is held under.
 *
 * Keyed on the run alone, deliberately. It used to include the failure code,
 * so a delivery that failed under a different name on each attempt bought
 * itself a fresh three every time - and the executor's codes are exactly the
 * thing that varies. Three attempts is three attempts for this run, whatever
 * it is called.
 */
export function attemptKey(runId: string): string {
  return runId;
}

/**
 * Whether to re-request this delivery now.
 *
 * `canRequest` is the run being at GATE_PASSED: a delivery is only claimed
 * there, so requesting one anywhere else books a record no worker will pick
 * up. `mayDeliver` is the caller's role - offering a write the backend is
 * certain to refuse is how someone learns their permissions by pressing
 * something irreversible.
 */
export function shouldRetry(
  delivery: DeliveryFacts | null | undefined,
  attempts: number,
  where: { canRequest: boolean; mayDeliver: boolean },
): boolean {
  if (!where.canRequest || !where.mayDeliver) return false;
  if (!isRecoverable(delivery)) return false;
  return attempts < MAX_ATTEMPTS;
}
